import type {
  BankLimitItemUpdateResponse,
  BankLimitStatus,
  BankLimitsUpdateScheduleResponse,
} from '@interfaces/bank-limits/responses/bank-limits.response';

/**
 * Formatação de exibição do card "Limites Bancários" da Home (card 3786). Só apresenta números que
 * o backend já mandou prontos (`GET dashboard/bank-limits`): nada aqui é cálculo financeiro.
 */

const MILLION = 1_000_000;

const compactFormatter = new Intl.NumberFormat('pt-BR', {
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
});

/** `52430000` → `"R$ 52,43 mi"`. Sem valor, `"—"`. */
export function formatCompactMillions(value: number | null | undefined): string {
  if (value === null || value === undefined || !Number.isFinite(value)) {
    return '—';
  }

  return `R$ ${compactFormatter.format(value / MILLION)} mi`;
}

/**
 * Largura da barra de uso (0 a 100), só para desenhar a barra: usado sobre o limite informado.
 * Sem limite (ou limite zero), 0.
 */
export function usagePercent(
  used: number | null | undefined,
  total: number | null | undefined
): number {
  if (
    !total ||
    total <= 0 ||
    !used ||
    used <= 0 ||
    !Number.isFinite(used) ||
    !Number.isFinite(total)
  ) {
    return 0;
  }

  return Math.min(100, Math.round((used / total) * 100));
}

/** `"2027-06-30"` (data de calendário, sem fuso) → `"30/06/2027"`. Inválida ou vazia, `""`. */
export function formatCalendarDate(value: string | null | undefined): string {
  const match = /^(\d{4})-(\d{2})-(\d{2})/.exec(value ?? '');
  return match ? `${match[3]}/${match[2]}/${match[1]}` : '';
}

/** O que a linha do banco diz quando o limite não entra na soma (vencido ou não informado). */
export interface BankLimitStatusText {
  headline: string;
  detail: string;
}

/**
 * Texto do banco fora da soma. `Ok` não tem aviso (`null`): a linha mostra o disponível e a barra.
 * Vencido sem data de vigência cai em "Vigência vencida", sem data.
 */
export function bankLimitStatusText(
  status: BankLimitStatus,
  validityEndDate: string | null | undefined,
  limit4131Total: number | null | undefined
): BankLimitStatusText | null {
  if (status === 'Expired') {
    const endDate = formatCalendarDate(validityEndDate);
    const lastLimit =
      limit4131Total !== null && limit4131Total !== undefined
        ? `Último limite: 4131 de ${formatCompactMillions(limit4131Total)}. `
        : '';

    return {
      headline: endDate ? `Vigência vencida em ${endDate}` : 'Vigência vencida',
      detail: `${lastLimit}Fica fora da soma até a vigência ser renovada.`,
    };
  }

  if (status === 'NotInformed') {
    return {
      headline: 'Limite não informado',
      detail: 'Há relacionamento cadastrado, mas nenhum limite de 4131 ou de derivativos.',
    };
  }

  return null;
}

const IGNORED_NAME_WORDS = new Set(['de', 'do', 'da', 'dos', 'das', 'e', 's.a.', 'sa', 's/a']);

/** Iniciais para o avatar do banco sem logo: `"Itaú Unibanco"` → `"IU"`, `"Bradesco"` → `"BR"`. */
export function bankInitials(bankName: string | null | undefined): string {
  const words = (bankName ?? '')
    .trim()
    .split(/\s+/)
    .filter(word => word && !IGNORED_NAME_WORDS.has(word.toLowerCase()));

  if (words.length === 0) {
    return '?';
  }

  if (words.length === 1) {
    return words[0].slice(0, 2).toUpperCase();
  }

  return `${words[0][0]}${words[1][0]}`.toUpperCase();
}

/**
 * Separa os bancos que ganham linha própria dos sem limite informado, que o card agrupa numa linha
 * só no fim (card 3786, E2E com 13 bancos NotInformed). Ganham linha própria, na ordem do backend:
 * Ok, Expired e o NotInformed com resposta do banco aguardando conferência, para o selo ficar à
 * vista em vez de escondido no grupo recolhido (card 3787, crítica de design G1). O NotInformed com
 * pedido em aberto continua no grupo: o resumo do grupo diz que o pedido saiu (A6).
 */
export function partitionBankLimits<
  T extends { status: BankLimitStatus; limitUpdate?: BankLimitItemUpdateResponse | null },
>(banks: readonly T[]): { listed: T[]; notInformed: T[] } {
  const listed: T[] = [];
  const notInformed: T[] = [];
  for (const bank of banks) {
    const grouped = bank.status === 'NotInformed' && !bank.limitUpdate?.pendingProposal;
    (grouped ? notInformed : listed).push(bank);
  }
  return { listed, notInformed };
}

/** `1` → `"1 banco sem limite informado"`, `13` → `"13 bancos sem limite informado"`. */
export function notInformedHeadline(count: number): string {
  return count === 1 ? '1 banco sem limite informado' : `${count} bancos sem limite informado`;
}

/** Segunda linha do grupo de bancos sem limite (dita uma vez, não por banco). */
export const NOT_INFORMED_GROUP_DETAIL =
  'Há relacionamento, mas nenhum limite de 4131 ou de derivativos cadastrado.';

/**
 * Segunda linha do grupo de bancos sem limite, com o pedido em aberto à vista sem abrir o grupo
 * (card 3787, crítica de design A6):
 * - nenhum banco com pedido em aberto: o texto fixo do grupo;
 * - todos no mesmo pedido: "Pedido enviado em dd/MM · RFQ #N · aguardando o banco" (o número da RFQ
 *   só para a equipe da Legatus, `canConfirm`, como no `bankLimitRequestView`);
 * - todos com pedido, em pedidos diferentes: "Pedido enviado aos N bancos · aguardando resposta";
 * - só alguns: "Pedido enviado a X de N bancos · aguardando resposta".
 * `formatShortDate` recebe o ISO UTC do envio e devolve "dd/MM".
 */
export function notInformedGroupDetail(
  banks: readonly { limitUpdate?: BankLimitItemUpdateResponse | null }[],
  permissions: BankLimitUpdatePermissions,
  formatShortDate: (isoDateTime: string) => string
): string {
  const open = banks
    .map(bank => bank.limitUpdate?.openRequest ?? null)
    .filter((request): request is NonNullable<typeof request> => request !== null);
  if (open.length === 0) {
    return NOT_INFORMED_GROUP_DETAIL;
  }
  const waiting = open.length === 1 ? 'aguardando o banco' : 'aguardando os bancos';
  if (open.length < banks.length) {
    return `Pedido enviado a ${open.length} de ${banks.length} bancos · aguardando resposta`;
  }
  const sameRequest = open.every(request => request.sequentialId === open[0].sequentialId);
  if (!sameRequest) {
    return `Pedido enviado aos ${open.length} bancos · aguardando resposta`;
  }
  const date = formatShortDate(open[0].sentAt);
  return permissions.canConfirm
    ? `Pedido enviado em ${date} · RFQ #${open[0].sequentialId} · ${waiting}`
    : `Pedido enviado em ${date} · ${waiting}`;
}

/** Logos empilhados do grupo: até `max` visíveis e quantos ficam no "+N". */
export function stackedLogos<T>(
  items: readonly T[],
  max = 5
): { visible: T[]; hiddenCount: number } {
  const visible = items.slice(0, Math.max(0, max));
  return { visible, hiddenCount: items.length - visible.length };
}

/** Mesmo endereço de logo do `app-bank-logo` (pelo código COMPE). */
const BANK_LOGO_BASE_URL = 'https://legatus.blob.core.windows.net/public-bank-logos';

/**
 * URL do logo do banco pelo código COMPE, ou `null` quando não há código válido. Diferente do
 * `app-bank-logo`, não cai no logo genérico (`0.png`): sem logo, o avatar mostra as iniciais.
 */
export function bankLogoUrl(bankCode: string | null | undefined): string | null {
  const code = parseInt(bankCode ?? '', 10);
  return Number.isFinite(code) && code > 0 ? `${BANK_LOGO_BASE_URL}/${code}.png` : null;
}

/** Cor da barra de uso: navy até 84%, âmbar a partir de 85% e vermelho no teto (crítica A11). */
export type UsageTone = 'normal' | 'warning' | 'danger';

export function usageTone(percent: number): UsageTone {
  if (percent >= 100) {
    return 'danger';
  }
  return percent >= 85 ? 'warning' : 'normal';
}

/* ------------------------------------------------------------------------------------------------
 * Pedido de atualização de limites ao banco (card 3787). Regras de exibição do botão, do selo e do
 * rodapé, por status e permissão. O backend manda tudo pronto (`limitUpdate`); aqui só se decide o
 * que mostrar.
 * --------------------------------------------------------------------------------------------- */

/** Permissões do usuário que mudam o card (espelho das claims do backend). */
export interface BankLimitUpdatePermissions {
  canRequest: boolean;
  canConfirm: boolean;
}

/** O que a linha do banco mostra para pedir a atualização. */
export type BankLimitRequestView =
  | { kind: 'button' }
  | { kind: 'no-contact'; text: string }
  | { kind: 'sent'; text: string; sequentialId: number | null };

export const LIMIT_REQUEST_BUTTON_LABEL = 'Pedir atualização ao banco';

/** Banco sem contato marcado: texto curto com o caminho, no lugar de um botão desabilitado (crítica G7). */
export const LIMIT_REQUEST_NO_CONTACT_TEXT = 'Nenhum contato recebe o pedido de limites';

/**
 * Pedido de um banco (só em `Expired` e `NotInformed`): botão, aviso de "sem contato" ou o texto do
 * pedido em aberto. O pedido em aberto aparece para todos; o número da RFQ (com link para a tela de
 * RFQs) só para a equipe da Legatus (`canConfirm`), que tem a tela (crítica A12c). O botão e o aviso
 * só com a permissão de pedir. `formatShortDate` recebe o ISO UTC do envio e devolve "dd/MM".
 */
export function bankLimitRequestView(
  status: BankLimitStatus,
  limitUpdate: BankLimitItemUpdateResponse | null | undefined,
  permissions: BankLimitUpdatePermissions,
  formatShortDate: (isoDateTime: string) => string
): BankLimitRequestView | null {
  if (status === 'Ok' || !limitUpdate) {
    return null;
  }

  const open = limitUpdate.openRequest;
  if (open) {
    const date = formatShortDate(open.sentAt);
    return permissions.canConfirm
      ? {
          kind: 'sent',
          text: `Pedido enviado em ${date} · RFQ #${open.sequentialId}`,
          sequentialId: open.sequentialId,
        }
      : { kind: 'sent', text: `Pedido enviado ao banco em ${date}`, sequentialId: null };
  }

  if (!permissions.canRequest) {
    return null;
  }

  return limitUpdate.canRequest
    ? { kind: 'button' }
    : { kind: 'no-contact', text: LIMIT_REQUEST_NO_CONTACT_TEXT };
}

/** Ação única do grupo de bancos sem limite (crítica G7): um botão para todos ou um aviso. */
export type NotInformedGroupAction =
  | { kind: 'button'; label: string; financialInstitutionIds: string[] }
  | { kind: 'no-contact'; text: string };

/**
 * Bancos sem limite pedem juntos: o botão vai para os que têm contato marcado e nenhum pedido em
 * aberto. Sem nenhum contato marcado, um aviso só. `null` sem permissão ou quando todos já foram pedidos.
 */
export function notInformedGroupAction(
  banks: readonly {
    financialInstitutionId: string;
    limitUpdate?: BankLimitItemUpdateResponse | null;
  }[],
  permissions: BankLimitUpdatePermissions
): NotInformedGroupAction | null {
  if (!permissions.canRequest) {
    return null;
  }
  const withData = banks.filter(bank => !!bank.limitUpdate);
  const requestable = withData.filter(
    bank => bank.limitUpdate!.canRequest && !bank.limitUpdate!.openRequest
  );
  if (requestable.length > 0) {
    return {
      kind: 'button',
      label:
        requestable.length === 1
          ? 'Pedir limites a 1 banco'
          : `Pedir limites aos ${requestable.length} bancos`,
      financialInstitutionIds: requestable.map(bank => bank.financialInstitutionId),
    };
  }
  const anyContact = withData.some(bank => bank.limitUpdate!.canRequest);
  return withData.length > 0 && !anyContact
    ? {
        kind: 'no-contact',
        text: 'Nenhum contato destes bancos recebe o pedido de limites. Marque a opção no contato em Relacionamento Bancário.',
      }
    : null;
}

/** Selo de resposta do banco aguardando conferência (qualquer status, inclusive `Ok`). */
export interface BankLimitProposalBadgeView {
  proposalId: string;
  label: string;
  /** Com `CONFIRM_LIMIT_UPDATE` o selo é botão e abre a modal de confirmação. */
  clickable: boolean;
  tooltip: string | null;
  needsReview: boolean;
}

export const PROPOSAL_WAITING_TOOLTIP =
  'O banco respondeu. A equipe da Legatus confere os valores antes de atualizar o limite.';

export function bankLimitProposalBadge(
  limitUpdate: BankLimitItemUpdateResponse | null | undefined,
  permissions: BankLimitUpdatePermissions
): BankLimitProposalBadgeView | null {
  const pending = limitUpdate?.pendingProposal;
  if (!pending) {
    return null;
  }

  return {
    proposalId: pending.proposalId,
    label: pending.hasExtractionError
      ? 'Resposta do banco precisa de conferência'
      : 'Resposta nova aguardando confirmação',
    clickable: permissions.canConfirm,
    tooltip: permissions.canConfirm
      ? 'Abrir os valores que o banco informou para conferir e confirmar.'
      : PROPOSAL_WAITING_TOOLTIP,
    needsReview: pending.hasExtractionError,
  };
}

/** Linha do rodapé sobre o pedido automático. */
export interface BankLimitScheduleView {
  text: string;
  tooltip: string;
  isOff: boolean;
}

const SCHEDULE_OWNER = 'Quem define é a equipe da Legatus.';

/** `30` → `"A cada 30 dias. Quem define é a equipe da Legatus."`. */
export function frequencyTooltip(frequencyDays: number | null): string {
  return frequencyDays === null
    ? `O pedido automático está desligado para esta empresa. ${SCHEDULE_OWNER}`
    : `A cada ${frequencyDays} dias. ${SCHEDULE_OWNER}`;
}

export function bankLimitScheduleView(
  schedule: BankLimitsUpdateScheduleResponse | null | undefined
): BankLimitScheduleView | null {
  if (!schedule) {
    return null;
  }

  if (schedule.frequencyDays === null) {
    return {
      text: 'Pedido automático aos bancos desligado',
      tooltip: frequencyTooltip(null),
      isOff: true,
    };
  }

  const nextDate = formatCalendarDate(schedule.nextRequestDate);
  return {
    text: nextDate
      ? `Próximo pedido aos bancos: ${nextDate}`
      : 'Nenhum contato marcado para receber pedido de atualização',
    tooltip: frequencyTooltip(schedule.frequencyDays),
    isOff: false,
  };
}

/** Rótulo de uma frequência: `60` → `"60 dias"`, `null` → `"Não enviar"`, `45` → `"45 dias"`. */
export function frequencyLabel(frequencyDays: number | null): string {
  return frequencyDays === null ? 'Não enviar' : `${frequencyDays} dias`;
}
